import { useState, useEffect } from 'react';
import { MapPin, Plus, Check, X, ExternalLink, Eye, Layers, Users, Grid3X3, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import api from '../../lib/api';

// Shape path generator (matching VenueEditor)
const getShapePath = (shape, width, height) => {
  switch (shape) {
    case 'rounded': 
      const r = Math.min(8, width/4, height/4); 
      return `M ${r} 0 L ${width-r} 0 Q ${width} 0 ${width} ${r} L ${width} ${height-r} Q ${width} ${height} ${width-r} ${height} L ${r} ${height} Q 0 ${height} 0 ${height-r} L 0 ${r} Q 0 0 ${r} 0 Z`;
    case 'circle': 
      const rx = width / 2; 
      const ry = height / 2;
      return `M ${rx} 0 A ${rx} ${ry} 0 1 1 ${rx} ${height} A ${rx} ${ry} 0 1 1 ${rx} 0 Z`;
    case 'hexagon': 
      return `M ${width*0.25} 0 L ${width*0.75} 0 L ${width} ${height*0.5} L ${width*0.75} ${height} L ${width*0.25} ${height} L 0 ${height*0.5} Z`;
    case 'triangle': 
      return `M ${width/2} 0 L ${width} ${height} L 0 ${height} Z`;
    case 'diamond': 
      return `M ${width/2} 0 L ${width} ${height/2} L ${width/2} ${height} L 0 ${height/2} Z`;
    case 'star': 
      let p = ''; 
      for (let i = 0; i < 10; i++) { 
        const r2 = i % 2 === 0 ? Math.min(width,height)/2 : (Math.min(width,height)/2)*0.4; 
        const a = (i*Math.PI/5)-Math.PI/2; 
        p += (i===0?'M ':'L ')+(width/2+r2*Math.cos(a))+' '+(height/2+r2*Math.sin(a))+' '; 
      } 
      return p+'Z';
    case 'trapezoid': 
      return `M ${width*0.15} 0 L ${width*0.85} 0 L ${width} ${height} L 0 ${height} Z`;
    case 'arc': 
      return `M 0 ${height} Q ${width*0.5} ${-height*0.3} ${width} ${height} L ${width*0.85} ${height} Q ${width*0.5} ${height*0.2} ${width*0.15} ${height} Z`;
    case 'lshape':
      return `M 0 0 L ${width*0.4} 0 L ${width*0.4} ${height*0.5} L ${width} ${height*0.5} L ${width} ${height} L 0 ${height} Z`;
    default: 
      return `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`;
  }
};

const VenueSelector = ({ selectedVenueId, onSelect, onVenueLoaded, className = '' }) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVenue, setExpandedVenue] = useState(null);
  
  useEffect(() => {
    loadVenues();
  }, []);
  
  // When a venue is selected, notify parent with full venue data
  useEffect(() => {
    if (selectedVenueId && onVenueLoaded) {
      const venue = venues.find(v => v.id === selectedVenueId);
      if (venue) {
        onVenueLoaded(venue);
      }
    }
  }, [selectedVenueId, venues, onVenueLoaded]);
  
  const loadVenues = async () => {
    try {
      const response = await api.get('/venues/producer');
      setVenues(response.data?.venues || []);
    } catch (error) {
      console.error('Error loading venues:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const selectedVenue = venues.find(v => v.id === selectedVenueId);
  
  // Types that are NOT sellable (structural/decorative/guide elements)
  const NON_SELLABLE_TYPES = [
    'wall', 'corner', 'pillar', 'bathroom', 'exit', 'stairs',  // Structural
    'bar', 'djbooth', 'stage',  // Guide/decorative (not audience areas)
    'decoration', 'sign', 'guide', 'info'  // Other non-sellable
  ];
  
  // Get SELLABLE sections (elements that can have tickets linked)
  const getVenueSections = (venue) => {
    // Support both 'sections' (API response) and 'elements' (legacy)
    const items = venue?.sections || venue?.elements || [];
    if (!items.length) return [];
    // Filter to only sellable elements with capacity
    return items.filter(el => 
      el.name &&
      !NON_SELLABLE_TYPES.includes(el.type) &&
      (el.capacity > 0 || el.type === 'section' || el.type === 'lounge' || el.type === 'table')
    );
  };
  
  // Get ALL elements for visual preview
  const getAllElements = (venue) => {
    const items = venue?.sections || venue?.elements || [];
    return items.filter(el => el.name);
  };
  
  // Full preview of venue with elements using SVG shapes
  const renderFullPreview = (venue) => {
    if (!venue) return null;
    
    // Use canvas dimensions from API, fallback to gridSize or defaults
    const canvasWidth = venue.canvas_width || venue.gridSize || 800;
    const canvasHeight = venue.canvas_height || venue.gridSize || 500;
    
    // Calculate scale to fit in preview container (max 300x200)
    const maxPreviewWidth = 300;
    const maxPreviewHeight = 200;
    const scaleX = maxPreviewWidth / canvasWidth;
    const scaleY = maxPreviewHeight / canvasHeight;
    const scale = Math.min(scaleX, scaleY);
    
    const previewWidth = canvasWidth * scale;
    const previewHeight = canvasHeight * scale;
    
    const sellableSections = getVenueSections(venue);
    const allItems = getAllElements(venue);
    
    return (
      <div className="mt-4 p-4 bg-black/30 rounded-lg border border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-white flex items-center gap-2">
            <Eye size={14} className="text-violet-400" />
            Vista Previa del Venue
          </h4>
          <span className="text-xs text-zinc-500">
            {sellableSections.length} vendibles / {allItems.length} total
          </span>
        </div>
        
        {/* Visual Map Preview using SVG */}
        <svg
          width={previewWidth}
          height={previewHeight}
          className="mx-auto bg-zinc-950 rounded-lg"
          style={{ border: '1px solid rgba(255,255,255,0.1)' }}
        >
          {allItems.map(el => {
            // Get position from either position object or direct x/y
            const x = (el.position?.x ?? el.x ?? 0) * scale;
            const y = (el.position?.y ?? el.y ?? 0) * scale;
            const width = Math.max((el.position?.width ?? el.width ?? 50) * scale, 6);
            const height = Math.max((el.position?.height ?? el.height ?? 50) * scale, 6);
            const rotation = el.rotation || 0;
            const shape = el.shape || 'rect';
            
            // Generate SVG path for this shape
            const shapePath = getShapePath(shape, width, height);
            const centerX = x + width / 2;
            const centerY = y + height / 2;
            
            return (
              <g 
                key={el.id}
                transform={rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined}
                className="transition-all hover:brightness-125"
              >
                <path
                  d={shapePath}
                  transform={`translate(${x}, ${y})`}
                  fill={el.color || '#4F46E5'}
                  opacity={0.8}
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="1"
                />
                {/* Show name if element is big enough */}
                {width > 35 && height > 20 && (
                  <text
                    x={x + width/2}
                    y={y + height/2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize={Math.min(7, width/8)}
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)', pointerEvents: 'none' }}
                  >
                    {el.name?.substring(0, 10)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        
        {/* Sections List */}
        {sellableSections.length > 0 && (
          <div className="mt-4">
            <h5 className="text-xs font-medium text-zinc-400 mb-2 flex items-center gap-1">
              <Layers size={12} />
              Secciones vendibles ({sellableSections.length})
            </h5>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {sellableSections.map(section => (
                <div 
                  key={section.id}
                  className="flex items-center gap-2 p-2 bg-white/5 rounded text-xs"
                >
                  <div 
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: section.color || '#4F46E5' }}
                  />
                  <span className="truncate text-zinc-300">{section.name}</span>
                  {section.capacity > 0 && (
                    <span className="text-zinc-500 ml-auto flex items-center gap-0.5">
                      <Users size={10} />
                      {section.capacity}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">
              Podrás vincular estas secciones con las entradas en el siguiente paso
            </p>
          </div>
        )}
        
        {sellableSections.length === 0 && allItems.length > 0 && (
          <p className="text-xs text-amber-500/80 mt-3 text-center">
            Este venue no tiene secciones vendibles (con capacidad)
          </p>
        )}
      </div>
    );
  };
  
  // Mini preview for list items using SVG shapes
  const renderMiniPreview = (venue) => {
    // Support both 'sections' (API response) and 'elements' (legacy)
    const items = venue?.sections || venue?.elements || [];
    if (!items.length) {
      return (
        <div className="w-full h-12 bg-zinc-900/50 rounded flex items-center justify-center">
          <span className="text-[10px] text-zinc-600">Sin elementos</span>
        </div>
      );
    }
    
    // Use canvas dimensions from API
    const canvasWidth = venue.canvas_width || venue.gridSize || 800;
    const canvasHeight = venue.canvas_height || venue.gridSize || 500;
    
    // Calculate scale to fit in mini preview (width: 100%, height: 48px)
    const previewHeight = 48;
    const scale = previewHeight / canvasHeight;
    const previewWidth = Math.min(canvasWidth * scale, 200);
    
    return (
      <svg
        width={previewWidth}
        height={previewHeight}
        className="mx-auto bg-black/50 rounded overflow-hidden"
      >
        {items.slice(0, 15).map(el => {
          // Get position from either position object or direct x/y
          const x = (el.position?.x ?? el.x ?? 0) * scale;
          const y = (el.position?.y ?? el.y ?? 0) * scale;
          const width = Math.max((el.position?.width ?? el.width ?? 50) * scale, 3);
          const height = Math.max((el.position?.height ?? el.height ?? 50) * scale, 3);
          const rotation = el.rotation || 0;
          const shape = el.shape || 'rect';
          
          // Generate SVG path for this shape
          const shapePath = getShapePath(shape, width, height);
          const centerX = x + width / 2;
          const centerY = y + height / 2;
          
          return (
            <g 
              key={el.id}
              transform={rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined}
            >
              <path
                d={shapePath}
                transform={`translate(${x}, ${y})`}
                fill={el.color || '#4F46E5'}
                opacity={0.7}
              />
            </g>
          );
        })}
      </svg>
    );
  };
  
  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-10 bg-white/5 rounded-lg"></div>
      </div>
    );
  }
  
  return (
    <div className={className}>
      {/* Selected venue display */}
      {selectedVenue ? (
        <div className="p-4 bg-zinc-900/50 border border-emerald-500/30 rounded-xl">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Check size={16} className="text-emerald-500" />
              </div>
              <div>
                <span className="font-medium text-sm text-white">{selectedVenue.name}</span>
                <p className="text-xs text-zinc-500">
                  {getVenueSections(selectedVenue).length} secciones vinculables
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onSelect(null)}
              className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400"
            >
              <X size={14} />
            </Button>
          </div>
          
          {/* Full preview when selected */}
          {renderFullPreview(selectedVenue)}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">
            Selecciona un venue para mostrar el mapa interactivo en la compra de tickets 
            y vincular las entradas con las secciones.
          </p>
          
          {venues.length === 0 ? (
            <div className="p-6 bg-zinc-900/30 border border-white/5 rounded-xl text-center">
              <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-3">
                <MapPin size={24} className="text-zinc-600" />
              </div>
              <p className="text-sm text-zinc-500 mb-3">No tienes venues creados</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('/producer/venues', '_blank')}
                className="text-xs"
              >
                <Plus size={12} className="mr-1" />
                Crear Venue
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
              {venues.map(venue => {
                const sections = getVenueSections(venue);
                const isExpanded = expandedVenue === venue.id;
                // Support both 'sections' (API response) and 'elements' (legacy)
                const allItems = venue.sections || venue.elements || [];
                
                return (
                  <div
                    key={venue.id}
                    className="bg-zinc-900/30 border border-white/5 rounded-xl overflow-hidden hover:border-white/20 transition-all"
                  >
                    {/* Venue header - clickable to select */}
                    <div 
                      className="p-3 cursor-pointer"
                      onClick={() => onSelect(venue.id)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: allItems[0]?.color || '#4F46E5' }}
                          />
                          <span className="text-sm font-medium text-white">{venue.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">
                            {sections.length} secciones
                          </span>
                          <Check size={14} className="text-emerald-500 opacity-0 group-hover:opacity-100" />
                        </div>
                      </div>
                      
                      {renderMiniPreview(venue)}
                      
                      <div className="flex items-center justify-between mt-2 text-[10px] text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Grid3X3 size={10} />
                          {allItems.length || 0} elementos
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={10} />
                          {venue.capacity || sections.reduce((sum, s) => sum + (s.capacity || 0), 0)} cap.
                        </span>
                      </div>
                    </div>
                    
                    {/* Expand/collapse button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedVenue(isExpanded ? null : venue.id);
                      }}
                      className="w-full py-1.5 bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center gap-1 text-[10px] text-zinc-500"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp size={12} />
                          Ocultar detalles
                        </>
                      ) : (
                        <>
                          <ChevronDown size={12} />
                          Ver secciones
                        </>
                      )}
                    </button>
                    
                    {/* Expanded sections list */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="p-3 border-t border-white/5 bg-black/20">
                            {sections.length > 0 ? (
                              <div className="space-y-1.5">
                                <p className="text-[10px] text-zinc-500 mb-2">
                                  Secciones que podrás vincular con entradas:
                                </p>
                                {sections.map(section => (
                                  <div 
                                    key={section.id}
                                    className="flex items-center gap-2 p-2 bg-white/5 rounded text-xs"
                                  >
                                    <div 
                                      className="w-3 h-3 rounded-sm flex-shrink-0"
                                      style={{ backgroundColor: section.color || '#4F46E5' }}
                                    />
                                    <span className="truncate text-zinc-300 flex-1">{section.name}</span>
                                    <span className="text-zinc-600 text-[10px]">{section.type}</span>
                                    {section.capacity > 0 && (
                                      <span className="text-zinc-500 flex items-center gap-0.5">
                                        <Users size={10} />
                                        {section.capacity}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-amber-500/80 text-center py-2">
                                Este venue no tiene secciones con nombre
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
          
          {venues.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => window.open('/producer/venues', '_blank')}
              className="w-full text-xs text-zinc-500 hover:text-white"
            >
              <ExternalLink size={12} className="mr-1" />
              Gestionar Venues
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default VenueSelector;
